import { memo } from "react";

import { externalLink } from "@/routes/_constants/external-link";

import { Checkbox } from "@/components/ui/input/checkbox";

type Props = {
	checkedItems: boolean[];
	onCheckedChange: (index: number, checked: boolean) => void;
};

type ChecklistLink = {
	text: string;
	href: string;
};

type ChecklistItem = {
	label: string;
	links?: ChecklistLink[];
};

/**
 * チェックポイントのマニュアル。管理画面内 FAQ は旧記事のままなので、サポートサイトを開く。
 * ベースURLは externalLink.faqSite（dev/prd で自動切替）を使う。
 */
const supportFaq = (path: string) => `${externalLink.faqSite}${path}`;

const CHECKLIST_ITEMS: ChecklistItem[] = [
	{
		label: "管理画面での顧客情報の確認方法",
		links: [
			{
				text: "管理画面での顧客情報の確認方法",
				href: supportFaq("/faq/host/reservation/0f5fffe15c5783fd9789814a81d35946"),
			},
		],
	},
	{
		label: "予約内容の変更・キャンセル方法",
		links: [
			{
				text: "予約内容の変更",
				href: supportFaq("/faq/host/reservation/c0ffffe15c578257bb4001aea475ecad"),
			},
			{
				text: "キャンセル方法",
				href: supportFaq("/faq/host/reservation/411fffe15c5782ec86910122e7e2ef10"),
			},
		],
	},
	{
		label: "返金処理の方法について",
		links: [
			{
				text: "返金処理の方法について",
				href: supportFaq("/faq/host/settlement/414fffe15c5783c4ad36012baef5b418"),
			},
		],
	},
	{
		label: "クレジットカードのオーソリゼーション（与信）について",
		links: [
			{
				text: "クレジットカードのオーソリゼーション（与信）について",
				href: supportFaq("/faq/host/reservation/7aefffe15c5783648d9e01cc5cc1a2a3"),
			},
		],
	},
];

export const STEP3_ITEM_COUNT = CHECKLIST_ITEMS.length;

export const Step3 = memo(({ checkedItems, onCheckedChange }: Props) => {
	return (
		<section className="space-y-4">
			<h3 className="border-b border-zinc-200 pb-2 text-base font-bold">
				STEP3. 予約後のオペレーションを事前に確認しましょう
			</h3>
			<p className="text-sm text-zinc-700">
				販売開始後に慌てないために、
				<span className="font-bold">予約成立後の対応フロー</span>
				も事前に確認しておきましょう。
			</p>
			<p className="text-sm font-bold text-zinc-800">チェックポイント</p>
			<div className="space-y-3">
				{CHECKLIST_ITEMS.map((item, index) => (
					<label
						key={item.label}
						className="flex cursor-pointer items-start gap-x-3 rounded-lg border border-zinc-200 p-4 hover:bg-zinc-50"
					>
						<Checkbox
							checked={checkedItems[index]}
							onCheckedChange={(checked) => onCheckedChange(index, checked === true)}
							className="mt-0.5"
						/>
						{/* リンクがある場合はリンクを表示 */}
						{item.links && item.links.length > 0 ? (
							<span className="text-sm leading-relaxed">
								{item.links.map((link, idx) => (
									<span key={link.href}>
										<a
											href={link.href}
											target="_blank"
											rel="noopener noreferrer"
											className="text-sm text-link-400 underline-offset-4 hover:underline"
											onClick={(e) => e.stopPropagation()}
										>
											{link.text}
										</a>
										{idx < item.links!.length - 1 && "・"}
									</span>
								))}
							</span>
						) : (
							<span className="text-sm leading-relaxed">{item.label}</span>
						)}
					</label>
				))}
			</div>
		</section>
	);
});

Step3.displayName = "Step3";
